'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './CurrencyConverter.module.scss';
import CurrencySelector from './currencySelector/currencySelector';
import getCurrencySymbol from './utils/getCurrencySymbol';
import sanitiseCurrencyInput from './utils/sanitiseCurrencyInput';
import currency from 'currency.js';
import Cta from '@JohnLewisPartnership/jlfs-ui-library/dist/components/cta/cta';
import { MappedFresCurrency } from '../../utils/getFresCurrencies/getFresCurrencies';
import { getFresQuote } from '../../actions/server/getFresQuote';
import { useDebounce } from '../../hooks/useDebounce/useDebounce';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { dispatchAnalyticsEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/components/analytics/Analytics';
import Message from '@JohnLewisPartnership/jlfs-ui-library/dist/components/message/message';
import { LoadingSpinner } from '@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner';
import Icon from '@JohnLewisPartnership/jlfs-ui-library/dist/components/icon/icon';
import { trackMonetateEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/utils/monetate/trackMonetateEvent';
import PaymentCards from '../PaymentCards/PaymentCards';
import buildSafeFresUrl from '../../utils/buildSafeFresUrl/buildSafeFresUrl';

export type TCurrencyConverterCCProps = {
  title: string;
  ctaLinkToSectionId?: string;
  enableRateSalePageCta?: boolean;
  currencyList: Array<MappedFresCurrency>;
  rateError?: boolean;
  fresUrl: string;
};

const GBP_SYMBOL = '£';
const MAX_VALUE = 1000000000;

const gbpCurrency: MappedFresCurrency = {
  countryCode: 'GB',
  currencyCode: 'GBP',
  currencyName: 'Great British Pounds',
  currencySymbol: GBP_SYMBOL,
  productId: undefined,
};

const extractCurrencyValue = (
  value: string,
  symbol: string
): string | undefined => {
  const parts = value.split(symbol);
  return parts.length > 1 ? parts[1] : undefined;
};

function CurrencyConverterCC({
  title,
  ctaLinkToSectionId = 'viewtodaysrates',
  enableRateSalePageCta = false,
  currencyList,
  rateError,
  fresUrl,
}: TCurrencyConverterCCProps) {
  const safeFresUrl = buildSafeFresUrl(fresUrl);

  const defaultSelectedCurrency = currencyList[0];
  const [selectedCurrency, setSelectedCurrency] = useState<MappedFresCurrency>(
    defaultSelectedCurrency
  );
  const [poundsInputValue, setPoundsInputValue] = useState<string>(GBP_SYMBOL);
  const [selectedCurrencyInputValue, setSelectedCurrencyInputValue] =
    useState<string>(
      getCurrencySymbol(defaultSelectedCurrency?.currencyCode ?? '')
    );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [quoteId, setQuoteId] = useState<string | undefined>(undefined);
  const [exchangeRate, setExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [apiError, setApiError] = useState<boolean>(false);
  const callIdRef = useRef(0);

  // Derived from selectedCurrency — no need to store separately as state
  const selectedCurrencySymbol = useMemo(
    () => getCurrencySymbol(selectedCurrency?.currencyCode ?? ''),
    [selectedCurrency]
  );

  const filteredCurrencyList = useMemo(
    () =>
      currencyList.filter(
        (c) => c.currencyCode !== selectedCurrency.currencyCode
      ),
    [currencyList, selectedCurrency]
  );

  const ctaTarget = enableRateSalePageCta
    ? '/travel-money/rate-table'
    : ctaLinkToSectionId.charAt(0) === '#'
      ? ctaLinkToSectionId
      : `#${ctaLinkToSectionId}`;

  const openNewTab = (e: React.MouseEvent) => {
    e.preventDefault();
    if (typeof window === 'undefined') return;

    const exchangeUrl = quoteId
      ? buildSafeFresUrl(fresUrl, quoteId)
      : safeFresUrl;

    if (!exchangeUrl) {
      return;
    }

    trackMonetateEvent('FXOrderNowHeroClick');

    dispatchAnalyticsEvent('currencyConverterOrderNowClick', {
      linkUrl: exchangeUrl,
      linkText: 'Order now',
      currency: selectedCurrency?.currencyCode || 'EUR',
      currencyType: '',
      sourceAmount: extractCurrencyValue(poundsInputValue, GBP_SYMBOL),
      destinationAmount:
        extractCurrencyValue(
          selectedCurrencyInputValue,
          selectedCurrencySymbol
        ) || '',
    });
    window.open(exchangeUrl, '_blank', 'noopener');
  };

  useEffect(() => {
    dispatchAnalyticsEvent('fxCalculatorCTA', {
      calculatorAction: 'Currency Change',
      calculatorDetail: selectedCurrency?.currencyCode || 'EUR',
    });
  }, [selectedCurrency]);

  const loadingSpinner = isLoading ? (
    <LoadingSpinner size="medium" className={styles.loadingSpinner} />
  ) : null;

  const callApi = async (
    productId: string,
    symbol: string,
    value: number,
    reverse: boolean,
    currencyCode: string
  ) => {
    const myCallId = ++callIdRef.current;
    setIsLoading(true);
    setApiError(false);
    try {
      const result = await getFresQuote(
        productId,
        Math.min(value, MAX_VALUE),
        reverse
      );
      if (myCallId !== callIdRef.current) return;
      const poundsFormatted = `${GBP_SYMBOL}${currency(result.poundsValue).format({ symbol: '' })}`;
      const currencyFormatted = `${symbol}${currency(result.currencyValue).format({ symbol: '' })}`;
      setPoundsInputValue(poundsFormatted);
      setSelectedCurrencyInputValue(currencyFormatted);
      setQuoteId(result.quoteId);
      setExchangeRate(result.exchangeRate);
      dispatchAnalyticsEvent('currencyConverterInteraction', {
        currency: currencyCode || 'EUR',
        currencyType: '',
        sourceAmount: extractCurrencyValue(poundsFormatted, GBP_SYMBOL),
        destinationAmount: extractCurrencyValue(currencyFormatted, symbol),
      });
    } catch {
      if (myCallId !== callIdRef.current) return;
      setApiError(true);
      setQuoteId(undefined);
      setExchangeRate(undefined);
    } finally {
      if (myCallId === callIdRef.current) setIsLoading(false);
    }
  };

  const { reset: debouncedApiCall, cancel: cancelDebouncedApiCall } =
    useDebounce(callApi, 1500);

  const handlePoundsInputValueUpdated = (value: string): void => {
    const sanitised = sanitiseCurrencyInput(GBP_SYMBOL, value);
    setPoundsInputValue(sanitised);
    setSelectedCurrencyInputValue(selectedCurrencySymbol);
    const numValue = currency(sanitised).value;

    if (numValue > 0) {
      debouncedApiCall(
        selectedCurrency.productId ?? selectedCurrency.currencyCode,
        selectedCurrencySymbol,
        numValue,
        false,
        selectedCurrency.currencyCode
      );
    } else {
      cancelDebouncedApiCall();
    }
  };

  const handleSelectedCurrencyInputValueUpdated = (value: string): void => {
    const sanitised = sanitiseCurrencyInput(selectedCurrencySymbol, value);
    setSelectedCurrencyInputValue(sanitised);
    setPoundsInputValue(GBP_SYMBOL);
    const numValue = currency(
      sanitised.replace(selectedCurrencySymbol, '')
    ).value;
    if (numValue > 0) {
      debouncedApiCall(
        selectedCurrency.productId ?? selectedCurrency.currencyCode,
        selectedCurrencySymbol,
        numValue,
        true,
        selectedCurrency.currencyCode
      );
    } else {
      cancelDebouncedApiCall();
    }
  };

  const handleNewCurrencySelected = (newCurrency: MappedFresCurrency): void => {
    setSelectedCurrency(newCurrency);
    const newSymbol = getCurrencySymbol(newCurrency.currencyCode ?? '');
    setSelectedCurrencyInputValue(newSymbol);
    setExchangeRate(undefined);
    const poundsValue = currency(poundsInputValue).value;
    const foreignValue = currency(
      selectedCurrencyInputValue.replace(selectedCurrencySymbol, '')
    ).value;
    if (poundsValue > 0) {
      callApi(
        newCurrency.productId ?? newCurrency.currencyCode ?? '',
        newSymbol,
        poundsValue,
        false,
        newCurrency.currencyCode ?? ''
      );
    } else if (foreignValue > 0) {
      callApi(
        newCurrency.productId ?? newCurrency.currencyCode ?? '',
        newSymbol,
        foreignValue,
        true,
        newCurrency.currencyCode ?? ''
      );
    }
  };

  const handlePoundsFocused = (value: string): void => {
    if (currency(value).value === 0) {
      setPoundsInputValue(GBP_SYMBOL);
    }
  };

  const handleForeignCurrencyFocused = (value: string): void => {
    if (currency(value).value === 0) {
      setSelectedCurrencyInputValue(selectedCurrencySymbol);
    }
  };

  return (
    <div className={styles.currencyConverter} data-testid="currency-converter">
      <div className={styles.topSection}>
        <h2 className={styles.title}>{title}</h2>
      </div>
      {rateError && (
        <div className={styles.errorMessage}>
          <Message
            title="We're unable to display exchange rates at the moment."
            type="warning"
          >
            <span>
              Please try again later or view our{' '}
              <a href={safeFresUrl || '#'} target="_blank" rel="noopener">
                order travel money page
              </a>
            </span>
          </Message>
        </div>
      )}
      <div className={styles.bottomSection}>
        {!rateError && (
          <span className={styles.inputSection}>
            <span className={styles.inputContainer}>
              <div className={styles.subheading}>How much would you like?</div>
              <div className={styles.infoText}>
                <Icon name="info" /> Minimum order £250 - maximum £2,500
              </div>
              <CurrencySelector
                selectedCurrency={gbpCurrency}
                inputValue={poundsInputValue}
                dropdownEnabled={false}
                inputValueUpdated={handlePoundsInputValueUpdated}
                focusUpdated={handlePoundsFocused}
                loadingSpinner={loadingSpinner}
              />
            </span>
            <span className={styles.separator}></span>
            <span className={styles.inputContainer}>
              <div className={styles.subheading}>
                What you&apos;ll get in your currency
              </div>
              <CurrencySelector
                currencyList={filteredCurrencyList}
                selectedCurrency={selectedCurrency}
                inputValue={selectedCurrencyInputValue}
                inputValueUpdated={handleSelectedCurrencyInputValueUpdated}
                selectedCurrencyUpdated={handleNewCurrencySelected}
                focusUpdated={handleForeignCurrencyFocused}
                loadingSpinner={loadingSpinner}
              />
            </span>
          </span>
        )}
        {!rateError && (
          <div className={styles.onlineRate}>
            <span>Online rate: </span>
            <span className={styles.sellRate}>
              {exchangeRate !== undefined
                ? `${GBP_SYMBOL}1 = ${selectedCurrencySymbol}${exchangeRate}`
                : `${GBP_SYMBOL}1 = `}
            </span>
          </div>
        )}
        {apiError && (
          <div className={styles.errorMessage}>
            <Message
              className={styles.content}
              title="Our currency calculator isn’t available at the moment"
              copy="You can still order now and choose your currency at the next step."
              type="warning"
              icon={null}
            />
          </div>
        )}
        {!rateError && (
          <div className={styles.todaysRatesCta}>
            <Cta
              className={styles.todaysRatesCta}
              type="underlined"
              target={ctaTarget}
              label="View today's rates"
            />
          </div>
        )}
        <span className={styles.ctaContainer}>
          <SecondaryButton
            data-testid="primary-button"
            tag="a"
            href=""
            target="_blank"
            inline={true}
            rel="noopener"
            onClick={openNewTab}
            className={styles.orderNowButton}
            tabIndex={0}
          >
            Order now
          </SecondaryButton>
          <PaymentCards theme="dark" />
        </span>
      </div>
    </div>
  );
}

export default CurrencyConverterCC;
